import React, { useState, useEffect, useCallback } from 'react';
import { Search, Bot, Loader2, AlertCircle, FileText, RefreshCw, Trash2, Upload, Database } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { openAIService } from '../../services/openai';
import { openAIAssistantsService } from '../../services/openai_assistants';
import * as PDFJS from 'pdfjs-dist/webpack';

// Initialize PDF.js worker
PDFJS.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS.version}/pdf.worker.min.js`;

interface Assistant {
  id: string;
  nombre: string;
  descripcion: string;
  vector_store_id?: string;
  vector_table_id?: string;
  api_configuracion_id: string;
  openai_assistant_id?: string;
}

interface VectorStore {
  id: string;
  name: string;
  description?: string;
}

interface VectorFile {
  id: string;
  name: string;
  size: number;
  type: string;
  uploaded_at: string;
  status: 'processing' | 'succeeded' | 'failed';
  vector_store_id: string;
  chunk_interno: boolean;
  vector_store?: VectorStore;
  assistant?: Assistant;
}

export default function VectorStore() {
  const [assistants, setAssistants] = useState<Assistant[]>([]);
  const [selectedAssistant, setSelectedAssistant] = useState<string>('');
  const [files, setFiles] = useState<VectorFile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');

  const fetchFiles = useCallback(async () => {
    if (!selectedAssistant) return;

    try {
      const assistant = assistants.find(a => a.id === selectedAssistant);
      if (!assistant || !assistant.vector_store_id) {
        setFiles([]);
        return;
      }

      const { data: documents, error: docsError } = await supabase
        .from('vector_store_documents')
        .select('*')
        .eq('vector_store_id', assistant.vector_store_id)
        .eq('asistente_id', assistant.id);

      if (docsError) throw docsError;

      const config = await openAIService.getConfig(assistant.api_configuracion_id);
      const vectorStore = await openAIService.getVectorStore(
        assistant.api_configuracion_id,
        assistant.vector_store_id
      );

      const filePromises = (documents || []).map(async doc => {
        try {
          const response = await fetch(`https://api.openai.com/v1/files/${doc.file_id}`, {
            headers: {
              'Authorization': `Bearer ${config.apiKey}`,
              'OpenAI-Beta': 'assistants=v2',
              ...(config.organizationId && { 'OpenAI-Organization': config.organizationId })
            }
          });

          if (!response.ok) {
            if (response.status === 404) {
              return {
                id: doc.file_id,
                filename: doc.nombre,
                bytes: 0,
                purpose: 'assistants',
                created_at: doc.created_at,
                status: 'failed'
              };
            }
            throw new Error(`Error fetching file status: ${response.statusText}`);
          }

          return response.json();
        } catch (error) {
          console.error(`Error checking file ${doc.file_id}:`, error);
          return {
            id: doc.file_id,
            filename: doc.nombre,
            bytes: 0,
            purpose: 'assistants',
            created_at: doc.created_at,
            status: 'failed'
          };
        }
      });

      const fileDetails = await Promise.all(filePromises);

      const vectorFiles = fileDetails.map((file, index) => ({
        id: file.id,
        name: file.filename,
        size: file.bytes,
        type: file.purpose,
        uploaded_at: file.created_at,
        status: file.status,
        vector_store_id: assistant.vector_store_id!,
        chunk_interno: documents![index].chunk_interno,
        vector_store: {
          id: vectorStore.id,
          name: vectorStore.name,
          description: vectorStore.description
        },
        assistant: {
          id: assistant.id,
          nombre: assistant.nombre,
          descripcion: assistant.descripcion
        }
      }));

      setFiles(prevFiles => {
        const hasChanges = JSON.stringify(prevFiles) !== JSON.stringify(vectorFiles);
        return hasChanges ? vectorFiles : prevFiles;
      });

    } catch (error: any) {
      console.error('Error fetching files:', error);
      setError(`Error al cargar los archivos: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  }, [selectedAssistant, assistants]);

  useEffect(() => {
    fetchAssistants();
  }, []);

  useEffect(() => {
    if (selectedAssistant) {
      fetchFiles();
    } else {
      setFiles([]);
    }
  }, [selectedAssistant, fetchFiles]);

  const fetchAssistants = async () => {
    try {
      const { data, error } = await supabase
        .from('asistentes')
        .select('*')
        .eq('estado', 'activo')
        .order('nombre');

      if (error) throw error;
      setAssistants(data || []);
    } catch (error: any) {
      console.error('Error fetching assistants:', error);
      setError('Error al cargar los asistentes');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    
    const invalidFiles = files.filter(file => !file.type.includes('pdf'));
    if (invalidFiles.length > 0) {
      setError('Solo se permiten archivos PDF');
      return;
    }

    const largeFiles = files.filter(file => file.size > 20 * 1024 * 1024);
    if (largeFiles.length > 0) {
      setError('Los archivos no deben superar los 20MB cada uno');
      return;
    }

    try {
      setIsUploading(true);
      setError('');

      const assistant = assistants.find(a => a.id === selectedAssistant);
      if (!assistant) throw new Error('Asistente no encontrado');

      const config = await openAIService.getConfig(assistant.api_configuracion_id);
      const fileIds: string[] = [];

      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('purpose', 'assistants');

        const response = await fetch('https://api.openai.com/v1/files', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${config.apiKey}`,
            ...(config.organizationId && { 'OpenAI-Organization': config.organizationId })
          },
          body: formData
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error?.message || `Error al subir el archivo ${file.name}`);
        }

        const data = await response.json();
        fileIds.push(data.id);
      }

      let vectorStoreId = assistant.vector_store_id;
      
      if (!vectorStoreId) {
        vectorStoreId = await openAIService.createVectorStore(
          assistant.api_configuracion_id,
          `${assistant.nombre}_vectors`,
          fileIds
        );

        const { error: updateError } = await supabase
          .from('asistentes')
          .update({ vector_store_id: vectorStoreId })
          .eq('id', assistant.id);

        if (updateError) throw updateError;
      } else {
        await openAIService.addFilesToVectorStore(
          assistant.api_configuracion_id,
          vectorStoreId,
          fileIds
        );
      }

      const documentsToInsert = files.map((file, index) => ({
        asistente_id: assistant.id,
        file_id: fileIds[index],
        nombre: file.name,
        tipo: 'general',
        estado: 'activo',
        vector_store_id: vectorStoreId,
        chunk_interno: false
      }));

      const { error: docsError } = await supabase
        .from('vector_store_documents')
        .insert(documentsToInsert);

      if (docsError) throw docsError;

      setSuccess(`${files.length} archivo(s) subido(s) correctamente`);
      fetchFiles();
    } catch (error: any) {
      console.error('Error uploading files:', error);
      setError(`Error al subir los archivos: ${error.message}`);
    } finally {
      setIsUploading(false);
      event.target.value = '';
    }
  };

  const handleDelete = async () => {
    if (selectedFiles.size === 0) {
      setError('Selecciona al menos un archivo para eliminar');
      return;
    }

    if (!confirm(`¿Estás seguro de eliminar ${selectedFiles.size} archivo(s)?`)) {
      return;
    }

    try {
      const assistant = assistants.find(a => a.id === selectedAssistant);
      if (!assistant) throw new Error('Asistente no encontrado');

      const config = await openAIService.getConfig(assistant.api_configuracion_id);

      for (const fileId of selectedFiles) {
        const response = await fetch(`https://api.openai.com/v1/files/${fileId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${config.apiKey}`,
            ...(config.organizationId && { 'OpenAI-Organization': config.organizationId })
          }
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error?.message || 'Error al eliminar el archivo');
        }

        await supabase
          .from('vector_store_documents')
          .delete()
          .eq('file_id', fileId);
      }

      setSuccess(`${selectedFiles.size} archivo(s) eliminado(s) correctamente`);
      setSelectedFiles(new Set());
      fetchFiles();
    } catch (error: any) {
      console.error('Error deleting files:', error);
      setError(`Error al eliminar los archivos: ${error.message}`);
    }
  };

  const toggleFileSelection = (fileId: string) => {
    setSelectedFiles(prev => {
      const next = new Set(prev);
      if (next.has(fileId)) {
        next.delete(fileId);
      } else {
        next.add(fileId);
      }
      return next;
    });
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const filteredFiles = files.filter(file => {
    if (searchTerm) {
      return (
        file.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        file.vector_store?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        file.assistant?.nombre.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return true;
  });

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <h1 className="text-2xl font-bold text-gray-800">Tienda de Vectores</h1>
          <select
            value={selectedAssistant}
            onChange={(e) => setSelectedAssistant(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm"
          >
            <option value="">Seleccionar asistente</option>
            {assistants.map(assistant => (
              <option key={assistant.id} value={assistant.id}>
                {assistant.nombre}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center space-x-2">
          <div className="relative">
            <input
              type="file"
              onChange={handleFileUpload}
              className="hidden"
              id="file-upload"
              multiple
              accept=".pdf"
              disabled={!selectedAssistant || isUploading}
            />
            <label
              htmlFor="file-upload"
              className={`px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 flex items-center space-x-2 cursor-pointer text-sm ${
                !selectedAssistant || isUploading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isUploading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Subiendo...</span>
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  <span>Subir PDFs</span>
                </>
              )}
            </label>
          </div>

          <button
            onClick={handleDelete}
            disabled={selectedFiles.size === 0 || isUploading}
            className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 flex items-center space-x-2 text-sm"
          >
            <Trash2 className="w-4 h-4" />
            <span>Eliminar ({selectedFiles.size})</span>
          </button>

          <button
            onClick={fetchFiles}
            disabled={!selectedAssistant || isLoading || isUploading}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
            title="Actualizar lista"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex items-center">
          <AlertCircle className="w-5 h-5 mr-2" />
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
          {success}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-6">
          <div className="flex items-center mb-4">
            <div className="relative flex-1">
              <input
                type="text"
                placeholder="Buscar archivos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left border-b">
                  <th className="pb-3 font-medium text-gray-600 w-8">
                    <input
                      type="checkbox"
                      checked={selectedFiles.size === filteredFiles.length && filteredFiles.length > 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedFiles(new Set(filteredFiles.map(f => f.id)));
                        } else {
                          setSelectedFiles(new Set());
                        }
                      }}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      disabled={filteredFiles.length === 0}
                    />
                  </th>
                  <th className="pb-3 font-medium text-gray-600">Nombre</th>
                  <th className="pb-3 font-medium text-gray-600">Tienda de Vectores</th>
                  <th className="pb-3 font-medium text-gray-600">Asistente</th>
                  <th className="pb-3 font-medium text-gray-600">Tamaño</th>
                  <th className="pb-3 font-medium text-gray-600">Estado</th>
                  <th className="pb-3 font-medium text-gray-600">Subido</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {filteredFiles.map((file) => (
                  <tr key={file.id} className="border-b">
                    <td className="py-2">
                      <input
                        type="checkbox"
                        checked={selectedFiles.has(file.id)}
                        onChange={() => toggleFileSelection(file.id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </td>
                    <td className="py-2">
                      <div className="flex items-center">
                        <FileText className="w-4 h-4 text-blue-500 mr-2" />
                        <span className="truncate max-w-xs" title={file.name}>{file.name}</span>
                      </div>
                    </td>
                    <td className="py-2">
                      <div className="flex items-center">
                        <Database className="w-4 h-4 text-purple-500 mr-2" />
                        <span className="font-medium">{file.vector_store?.name || '-'}</span>
                      </div>
                    </td>
                    <td className="py-2">
                      <div className="flex items-center">
                        <Bot className="w-4 h-4 text-blue-500 mr-2" />
                        <span>{file.assistant?.nombre || '-'}</span>
                      </div>
                    </td>
                    <td className="py-2">{formatFileSize(file.size)}</td>
                    <td className="py-2">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        file.status === 'succeeded'
                          ? 'bg-green-100 text-green-800'
                          : file.status === 'failed'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {file.status === 'succeeded' ? 'Completado' : 
                         file.status === 'failed' ? 'Error' : 'Procesando'}
                      </span>
                    </td>
                    <td className="py-2 text-gray-600">
                      {new Date(file.uploaded_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
                {filteredFiles.length === 0 && (
                  <tr>
                    <td colSpan={7} className="text-center py-8 text-gray-500">
                      <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No hay archivos en la tienda de vectores</p>
                      {!selectedAssistant && (
                        <p className="text-sm mt-2">Selecciona un asistente para ver sus archivos</p>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}